/**
 * AreaChart — C10 "האם המנויים גדלים?" (MRR, 12 months). One series, one y-axis, a direct
 * end label, focusable per-month hit areas that share the tooltip, and a table twin.
 */

import { ChartBlock, type TableTwin } from './ChartBlock.tsx';
import { ChartTip, useTooltip, type TipContent } from './Tooltip.tsx';
import { areaPath, linePath, niceTicks } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';
import { fmtK, fmtMoney } from '../format.ts';

export function AreaChart({
  labels,
  values,
  height = 150,
  labelEvery = 3,
  summary,
  table,
  tip,
}: {
  labels: string[];
  /** agorot */
  values: number[];
  height?: number;
  labelEvery?: number;
  summary: string;
  table: TableTwin;
  tip: (index: number) => TipContent;
}) {
  const [ref, width] = useChartWidth(480);
  const tooltip = useTooltip();
  if (values.length < 2) return <div ref={ref} />;

  const W = Math.max(width, 200);
  const mL = 40;
  const mR = 14;
  const mT = 20;
  const mB = 22;
  const pw = W - mL - mR;
  const ph = height - mT - mB;
  const ticks = niceTicks(0, Math.max(...values) * 1.15, 4);
  const x = (i: number) => mL + (i / (values.length - 1)) * pw;
  const y = (v: number) => mT + ph - (v / (ticks.max || 1)) * ph;
  const coords = values.map((v, i) => [x(i), y(v)] as const);
  const lastIndex = values.length - 1;
  const slot = pw / (values.length - 1);

  return (
    <div ref={ref}>
      <ChartBlock summary={summary} table={table}>
        <div className="plot">
          <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height}>
            <g className="grid">
              {ticks.ticks.map((t) => (
                <line key={t} x1={mL} x2={W - mR} y1={y(t)} y2={y(t)} />
              ))}
            </g>
            <g className="axis">
              {ticks.ticks.map((t) => (
                <text key={t} x={mL - 6} y={y(t) + 3.5} textAnchor="end" className="lbl2">
                  {fmtK(t)}
                </text>
              ))}
            </g>
            <path d={areaPath(coords, mT + ph)} fill="var(--series-1)" opacity=".1" />
            <path d={linePath(coords)} fill="none" stroke="var(--series-1)" strokeWidth="2" strokeLinejoin="round" />
            {labels.map((label, i) =>
              i % labelEvery === 0 || i === lastIndex ? (
                <text key={label + i} x={x(i)} y={height - 7} textAnchor="middle" className="lbl3">
                  {label}
                </text>
              ) : null,
            )}
            <circle cx={x(lastIndex)} cy={y(values[lastIndex]!)} r="4.5" fill="var(--series-1)" className="ring" />
            <text x={x(lastIndex) - 8} y={y(values[lastIndex]!) - 9} textAnchor="end" className="lbl">
              {fmtMoney(values[lastIndex]!)}
            </text>
            {values.map((_v, i) => {
              const content = tip(i);
              const handlers = tooltip.bind(content, { x: x(i), y: mT });
              return (
                <rect
                  key={`hit-${i}`}
                  className="bar-hit"
                  x={x(i) - slot / 2}
                  y={mT}
                  width={slot}
                  height={ph}
                  tabIndex={0}
                  role="img"
                  aria-label={`${content.title}: ${content.rows.map((r) => `${r.label} ${r.value}`).join(', ')}`}
                  {...handlers}
                />
              );
            })}
          </svg>
        </div>
        <ChartTip state={tooltip.state} width={W} />
      </ChartBlock>
    </div>
  );
}
